import React, { memo } from "react";
import {
    Autocomplete,
    TextField,
    Typography,
    Stack,
} from "@mui/material";

/**
 * UncontrolledAutocomplete
 *
 * A standalone (non-react-hook-form) MUI Autocomplete with the same visual
 * language as CustomAutocomplete. Use inside modals / drawers that manage
 * state locally rather than through a form context.
 *
 * Props:
 *   label        {string}   — floating label / placeholder prefix
 *   placeholder  {string}   — input placeholder
 *   options      {array}    — option list  [{value, label, ...}]
 *   value        {any}      — currently selected option (controlled)
 *   onChange     {fn(val)}  — called with the selected option object
 *   getOptionLabel {fn}     — override default label resolver
 *   renderOption  {fn}      — override default option renderer
 *   filterOptions {fn}      — override default filter
 *   isOptionEqualToValue {fn} — equality check
 *   loading      {boolean}
 *   disabled     {boolean}
 *   error        {string}   — error message (non-empty → red border + helper)
 *   size         {"small"|"medium"}
 *   ...rest                 — forwarded to <Autocomplete>
 */
const UncontrolledAutocomplete = ({
    label        = "",
    placeholder  = "",
    options      = [],
    value        = null,
    onChange,
    getOptionLabel,
    renderOption,
    filterOptions,
    isOptionEqualToValue,
    loading      = false,
    disabled     = false,
    error        = "",
    size         = "small",
    ...rest
}) => {
    const resolveLabel = getOptionLabel ?? ((opt) =>
        typeof opt === "string" ? opt : opt?.label ?? ""
    );

    return (
        <Autocomplete
            options={options}
            value={value}
            onChange={(_, newVal) => onChange?.(newVal)}
            getOptionLabel={resolveLabel}
            renderOption={renderOption}
            filterOptions={filterOptions}
            isOptionEqualToValue={
                isOptionEqualToValue ??
                ((opt, val) =>
                    opt?.value === val?.value || opt === val)
            }
            loading={loading}
            disabled={disabled}
            size={size}
            sx={{ width: "100%", ...rest.sx }}
            {...rest}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder || (label ? `Select ${label}` : "")}
                    error={Boolean(error)}
                    helperText={error || ""}
                    variant="outlined"
                    size={size}
                    fullWidth
                    sx={{
                        '& .MuiInputBase-root': {
                            fontSize: '12px',
                            borderRadius: '8px',
                            backgroundColor: '#f8fafc',
                        },
                    }}
                    slotProps={{
                        htmlInput: {
                            ...params.inputProps,
                            style: { fontSize: "12px" },
                        },
                    }}
                />
            )}
        />
    );
};

export default memo(UncontrolledAutocomplete);
